import { viewerApi } from '@viewer/lib/viewerApi'
import { collectWorldTriangles, type WorldTrianglesSource } from '@viewer/lib/worldTriangles'
import { namesFor } from '@viewer/clash/clashModels'
import { pointCloudApi } from '../pointCloudApi'
import { elementosEscolhidos } from './compararNuvem'
import { PADRAO_ACO } from './escolha'
import { type CasoConhecido, nuvemDeExemplo } from './nuvemSintetica'

/** Criar a nuvem de exemplo a partir do modelo aberto, e lembrar o que
 *  lá se pôs para o painel o poder mostrar ao lado do relatório. */

export const NOME_DA_NUVEM_DE_EXEMPLO = 'Nuvem de exemplo (sintética).pts'

const casosPorNuvem = new Map<string, CasoConhecido[]>()

export function casosDaNuvem(cloudId: string): CasoConhecido[] | null {
  return casosPorNuvem.get(cloudId) ?? null
}

async function triangulos(
  model: NonNullable<ReturnType<typeof viewerApi.getModel>>,
  ids: number[],
  origem: [number, number, number],
) {
  if (ids.length === 0) return []
  const r = await collectWorldTriangles(model as unknown as WorldTrianglesSource, { ids, origin: origem })
  return r.items.filter((i) => i.indices.length >= 3)
}

export async function criarNuvemDeExemplo(modelId: string): Promise<void> {
  const model = viewerApi.getModel(modelId)
  if (!model) throw new Error('Abre primeiro um modelo.')

  // O aço primeiro, que é o caso de uso. Se o modelo não tiver aço, os
  // pilares e as vigas que houver.
  let pil = await elementosEscolhidos(modelId, ['IFCCOLUMN'], PADRAO_ACO)
  let vig = await elementosEscolhidos(modelId, ['IFCBEAM', 'IFCMEMBER'], PADRAO_ACO)
  if (pil.ids.length < 4) {
    pil = await elementosEscolhidos(modelId, ['IFCCOLUMN'], '')
    vig = await elementosEscolhidos(modelId, ['IFCBEAM', 'IFCMEMBER'], '')
  }
  if (pil.ids.length < 4) {
    throw new Error(
      'A nuvem de exemplo precisa de um modelo com pelo menos quatro pilares. Experimenta com o modelo de exemplo.',
    )
  }

  model.object.updateMatrixWorld(true)
  const mw = model.object.matrixWorld
  const origem: [number, number, number] = [mw.elements[12], mw.elements[13], mw.elements[14]]
  const pilares = await triangulos(model, pil.ids, origem)
  const vigas = await triangulos(model, vig.ids, origem)
  const { pontos, casos } = nuvemDeExemplo(pilares, vigas)

  // Os pontos voltam às coordenadas de um ficheiro de levantamento: o
  // inverso da colocação do modelo, e com o Z para cima. Assim a nuvem
  // entra pelo mesmo caminho de um PTS a sério, e o "Alinhar ao modelo"
  // põe-na no sítio.
  const inv = mw.clone().invert().elements
  const linhas: string[] = [String(pontos.length / 3)]
  for (let i = 0; i < pontos.length; i += 3) {
    const x = pontos[i] + origem[0], y = pontos[i + 1] + origem[1], z = pontos[i + 2] + origem[2]
    const lx = inv[0] * x + inv[4] * y + inv[8] * z + inv[12]
    const ly = inv[1] * x + inv[5] * y + inv[9] * z + inv[13]
    const lz = inv[2] * x + inv[6] * y + inv[10] * z + inv[14]
    linhas.push(`${lx.toFixed(4)} ${(-lz).toFixed(4)} ${ly.toFixed(4)} 0 185 190 200`)
  }
  const ficheiro = new File([linhas.join('\n')], NOME_DA_NUVEM_DE_EXEMPLO, { type: 'text/plain' })

  const nomes = await namesFor(modelId, casos.map((c) => c.localId))
  const comNomes = casos.map((c, i) => ({
    ...c,
    descricao: `${nomes[i] ?? `#${c.localId}`}: ${c.descricao}`,
  }))
  const entrada = await pointCloudApi.abrir(ficheiro)
  if (!entrada) throw new Error(pointCloudApi.ultimoErro() ?? 'Não foi possível criar a nuvem de exemplo.')
  casosPorNuvem.set(entrada.id, comNomes)
  // Alinhar avisa a lista, e o painel volta a desenhar-se já com os casos.
  pointCloudApi.alinharAoModelo(entrada.id, modelId)
  pointCloudApi.enquadrar(entrada.id)
}
